import React, { useMemo } from "react";
import { Box, Card, CardBody, Heading } from "@chakra-ui/react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { STATUS_COLORS, STATUS_STROKES } from "@config/chartConfig";
import type { MappedDashboardStats } from "@modules/ytg/hooks/useYtgDashboardStats";

const STATUS_ROWS: Array<{ key: string; label: string }> = [
    { key: "completed", label: "Terminées" },
    { key: "in_progress", label: "En cours" },
    { key: "pending", label: "En attente" },
    { key: "failed", label: "Échouées" },
];

interface YtgStatusChartProps {
    stats: MappedDashboardStats | null | undefined;
}

/**
 * Global dashboard donut chart: breakdown of operations by status. Zero-value
 * statuses are removed from the chart but remain in the legend. Renders nothing
 * until the statistics are available.
 */
export const YtgStatusChart = ({ stats }: YtgStatusChartProps): JSX.Element | null => {
    const data = useMemo(() => {
        if (!stats) return null;
        // Drop 0-value entries — recharts renders them as zero-width slices
        // that leave visible gaps and an incomplete donut. The legend below
        // still lists every status (full reference, like chart.js did).
        return STATUS_ROWS.map((row) => ({
            name: row.label,
            value: (stats.by_status as Record<string, number>)[row.key] ?? 0,
            fill: STATUS_COLORS[row.key],
            stroke: STATUS_STROKES[row.key],
        })).filter((entry) => entry.value > 0);
    }, [stats]);

    const legendData = useMemo(
        () =>
            STATUS_ROWS.map((row) => ({
                value: row.label,
                type: "square" as const,
                color: STATUS_COLORS[row.key],
            })),
        [],
    );

    if (!data) return null;

    return (
        <Card>
            <CardBody>
                <Heading as="h2" size="md" mb={4} color="gray.700">
                    Opérations par statut
                </Heading>
                <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="50%"
                                outerRadius="80%"
                                strokeWidth={2}
                            >
                                {data.map((entry) => (
                                    <Cell
                                        key={entry.name}
                                        fill={entry.fill}
                                        stroke={entry.stroke}
                                    />
                                ))}
                            </Pie>
                            <Tooltip labelFormatter={() => ""} />
                            <Legend
                                payload={legendData}
                                layout="vertical"
                                align="right"
                                verticalAlign="middle"
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            </CardBody>
        </Card>
    );
};
